import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  buildSimilarProductFilters,
  buildSimilarProductsPath,
} from "../../utils/similarProducts";

/**
 * 渲染商品属性表，并提供相似产品筛选入口。
 * @param {{ attributes?: Record<string, any>, product?: Record<string, any> }} props 组件属性
 * @returns {React.JSX.Element} 商品属性表
 */
const ProductAttributes = ({ attributes = {}, product = {} }) => {
  const navigate = useNavigate();
  const similarProductFilters = buildSimilarProductFilters(product);
  const filterableAttributeNames = Object.keys(similarProductFilters);
  const hasSimilarFilters = filterableAttributeNames.length > 0;
  const [isFilterEnabled, setIsFilterEnabled] = useState(true);
  const [selectedAttributeNames, setSelectedAttributeNames] = useState([]);
  const selectedSimilarFilters = buildSimilarProductFilters(
    product,
    selectedAttributeNames
  );
  const canViewSimilar =
    isFilterEnabled && Object.keys(selectedSimilarFilters).length > 0;

  /** 商品切换时恢复相似筛选的初始选择状态。 */
  useEffect(() => {
    setIsFilterEnabled(true);
    setSelectedAttributeNames([]);
  }, [product?._id]);

  /**
   * 启用或关闭相似产品属性选择。
   * @param {React.ChangeEvent<HTMLInputElement>} event 复选框变更事件
   * @returns {void}
   */
  const handleFilterEnabledChange = (event) => {
    const isEnabled = event.target.checked;

    setIsFilterEnabled(isEnabled);
    if (!isEnabled) {
      setSelectedAttributeNames([]);
    }
  };

  /**
   * 切换单个商品属性的筛选选中状态。
   * @param {string} attributeName 商品属性名称
   * @returns {void}
   */
  const handleAttributeChange = (attributeName) => {
    setSelectedAttributeNames((currentNames) =>
      currentNames.includes(attributeName)
        ? currentNames.filter((name) => name !== attributeName)
        : [...currentNames, attributeName]
    );
  };

  /**
   * 跳转到商品列表页，并仅用已勾选的当前商品属性筛选相似产品。
   * @returns {void}
   */
  const handleFilterSimilarProducts = () => {
    if (!canViewSimilar) return;

    navigate(buildSimilarProductsPath(product, selectedAttributeNames));
  };

  return (
    <div className="mt-6 md:mt-10">
      <div className="mb-6 md:mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="font-bold text-lg md:text-2xl text-gray-900">
          Product Attributes
        </h2>
        <label
          className={`inline-flex min-h-[40px] items-center gap-2 border px-3 py-2 text-sm font-semibold ${
            hasSimilarFilters
              ? "cursor-pointer border-[#2684fe] text-gray-900 hover:bg-[#F0F6FF]"
              : "cursor-not-allowed border-gray-200 text-gray-400"
          }`}
          title={
            hasSimilarFilters
              ? "Filter products with the same attributes"
              : "No attributes available for similar product filtering"
          }
        >
          <input
            type="checkbox"
            disabled={!hasSimilarFilters}
            checked={hasSimilarFilters && isFilterEnabled}
            onChange={handleFilterEnabledChange}
            className="h-4 w-4 accent-[#2684fe]"
          />
          <span>Filter Similar Products</span>
        </label>
      </div>
      <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.5rem] md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_12rem]">
        <div className="font-bold text-sm md:text-base py-3 border-b border-gray-100 text-gray-900">Type</div>
        <div className="font-bold text-sm md:text-base py-3 border-b border-gray-100 text-gray-900">Description</div>
        <div className="border-b border-gray-100" aria-hidden="true" />
        {Object.entries(attributes).length > 0 ? (
          Object.entries(attributes).map(([key, value]) => {
            const canFilterAttribute = filterableAttributeNames.includes(key);
            const isSelected = selectedAttributeNames.includes(key);

            return (
              <React.Fragment key={key}>
                <div className="py-2 md:py-3 border-b border-gray-50 text-gray-600 text-xs md:text-sm font-medium pr-4">{key}</div>
                <div className="py-2 md:py-3 border-b border-gray-50 text-gray-800 text-xs md:text-sm">{value || "-"}</div>
                {isFilterEnabled ? (
                  <label
                    className={`flex items-center justify-center border-b border-gray-50 ${
                      canFilterAttribute
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    }`}
                    title={
                      canFilterAttribute
                        ? `Filter by ${key}: ${value}`
                        : "This attribute cannot be used for filtering"
                    }
                  >
                    <input
                      type="checkbox"
                      aria-label={`Filter by ${key}: ${value}`}
                      disabled={!canFilterAttribute}
                      checked={canFilterAttribute && isSelected}
                      onChange={() => handleAttributeChange(key)}
                      className="h-4 w-4 accent-[#2684fe] disabled:cursor-not-allowed"
                    />
                  </label>
                ) : (
                  <div className="border-b border-gray-50" aria-hidden="true" />
                )}
              </React.Fragment>
            );
          })
        ) : (
          <p className="col-span-3 py-4 text-gray-400 italic text-sm">No attributes available.</p>
        )}
      </div>
      {hasSimilarFilters && isFilterEnabled && (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">
            Select one or more attributes to find similar products.
          </p>
          <button
            type="button"
            disabled={!canViewSimilar}
            onClick={handleFilterSimilarProducts}
            className="min-h-[42px] border border-[#2684fe] px-6 py-2 text-sm font-bold text-[#2684fe] transition-colors hover:bg-[#F0F6FF] disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
          >
            View Similar
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductAttributes;
